import type { ProgressButtonState } from "@bankmonitor/bm-ui-kit";
import { Spinner, Button, Alert, getUrlParameter } from "@bankmonitor/bm-ui-kit";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import React from "react";
import { Navigate } from "react-router-dom";
import imgMfa from "@asset/img/mfa.svg";
import imgRegistration from "@asset/img/registration.svg";
import Notifications from "@component/Notifications";
import MfaWithRedirect from "@page/auth/component/MfaWithRedirect";
import RegistrationForm from "@page/auth/component/RegistrationForm";
import type { FormFields } from "@page/auth/component/RegistrationForm";
import type { PasswordCredentials } from "@page/auth/signInForMfa";
import { signInForMfa } from "@page/auth/signInForMfa";
import AuthContext from "@service/AuthenticationContext";
import AuthenticationService from "@service/AuthenticationService";
import { Measurement } from "@util/measurement/Measurement";
import { logError } from "@util/sentry-error";
import type { AbstractPageProps } from "../AbstractPage";
import { AbstractAuthPage } from "./AbstractAuthPage";
import type { AbstractAuthPageState } from "./AbstractAuthPage";

const DEAL_LIST_PATH = "/";
const LOGIN_PATH = "/";
const REGISTRATION_TOAST_HOLD_MS = 2_000;

interface State extends AbstractAuthPageState {
	loginRequired: boolean;
	showMFAComponent: boolean;
	isKeyValid: boolean;
	initialValues: FormFields;
	status: ProgressButtonState;
}

export default class RegistrationPage extends AbstractAuthPage<AbstractPageProps, State> {
	protected readonly pageTitle = "Ügyfélportál regisztráció";
	protected readonly pageLead = "Gyorsítsd fel az ügyintézés folyamatát!";
	private emailToken = getUrlParameter("token");

	declare context: React.ContextType<typeof AuthContext>;
	public static contextType = AuthContext;

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			loginRequired: false,
			showMFAComponent: false,
			isKeyValid: undefined,
			status: undefined,
			initialValues: {
				lastName: "",
				firstName: "",
				userEmail: "",
				userPass: "",
				userPass1: "",
			},
		};
	}

	public componentDidMount(): void {
		this.emailToken = getUrlParameter("token");
		super.componentDidMount();

		this.validateToken();
	}

	private validateToken(): void {
		const email = getUrlParameter("email");

		AuthenticationService.validateEmailToken(email, this.emailToken)
			.then((response) => {
				this.setState({
					isKeyValid: true,
					initialValues: {
						lastName: response.lastName,
						firstName: response.firstName,
						userEmail: email,
						userPass: "",
						userPass1: "",
					},
				});
			})
			.catch((response: Error) => {
				const { message = "Váratlan hiba történt!" } = response;

				Notifications.pushDangerMessage(message);

				this.setState({
					status: "error",
					isKeyValid: false,
				});
			});
	}

	@autobind
	private handleSubmit(values: FormFields): void {
		this.setState({
			status: "loading",
			initialValues: values,
		});
		const { userEmail, userPass } = values;

		this.promisePool
			.add(AuthenticationService.registration(userEmail, userPass, this.emailToken))
			.then(() => {
				Measurement.sendEvent("BM_signUp");

				this.continueToMfa({ email: userEmail, password: userPass });
			})
			.catch((response: Error) => {
				const { message = "Váratlan hiba történt!" } = response;

				Notifications.pushDangerMessage(message);

				this.setState({
					status: "error",
				});

				this.promisePool.addTimeout().then(() => {
					this.setState({
						status: undefined,
					});
				});
			});
	}

	private continueToMfa(credentials: PasswordCredentials): void {
		signInForMfa({ auth: this.context, promisePool: this.promisePool }, credentials)
			.then(() => {
				this.setState({
					showMFAComponent: true,
					status: "done",
				});
			})
			.catch((error: Error) => {
				logError(error);
				Notifications.pushSuccessMessage("Sikeres regisztráció! Kérjük lépjen be!");

				this.setState({
					loginRequired: true,
				});
			});
	}

	@autobind
	private announceRegistration(): PromiseLike<unknown> {
		Notifications.pushSuccessMessage("Sikeres regisztráció!");

		return this.promisePool.addTimeout(REGISTRATION_TOAST_HOLD_MS);
	}

	protected renderLeftColumn(): ReactNode {
		const { showMFAComponent } = this.state;
		const [image, alt] = showMFAComponent ? [imgMfa, "mfa"] : [imgRegistration, "regisztráció"];

		return (
			<div className="d-none d-md-block text-center">
				<img src={image} alt={alt} width="75%" />
			</div>
		);
	}

	protected renderRightColumn(): ReactNode {
		const { showMFAComponent, initialValues, isKeyValid, status } = this.state;
		const { userData } = this.context;

		if (showMFAComponent) {
			return <MfaWithRedirect phone={userData?.phone} redirectTo={DEAL_LIST_PATH} onCodeAccepted={this.announceRegistration} />;
		}

		if (isKeyValid === undefined) {
			return (
				<div className="text-center py-5">
					<Spinner size="2x" variant="primary" />
					<div className="mt-3">Ellenőrzés...</div>
				</div>
			);
		}

		if (isKeyValid === false) {
			return (
				<>
					<Alert icon="auto" variant="danger">
						<Alert.Heading>Hibás link!</Alert.Heading>
						<p>Érvénytelen regisztrációs link, kérlek vedd fel a kapcsolatot az ügyintéződdel!</p>
					</Alert>
					<Button icon={<FontAwesomeIcon icon={solid("right-to-bracket")} />} type="button" href="/" variant="outline-primary" className="w-100">
						Belépés
					</Button>
				</>
			);
		}

		return <RegistrationForm onSubmit={this.handleSubmit} initialValues={initialValues} registerButtonStatus={status} />;
	}

	public render(): ReactNode {
		if (AuthenticationService.hasMFA()) {
			return <Navigate to="/" />;
		}

		if (this.state.loginRequired) {
			return <Navigate to={AuthenticationService.getRedirectWithParamsUrl(LOGIN_PATH)} replace />;
		}

		return super.render();
	}
}
